import { Button } from "@/components/Button";
import Modal from "@/components/Modal/Modal";
import { usePostDeleteTeamMember } from "@/requests/hooks/user";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { tv } from "tailwind-variants";

interface IProps {
  isOpen: boolean;
  closeModal: () => void;
  email: string;
  publicKey?: string;
}

const styles = tv({
  slots: {
    base: "flex flex-col items-center justify-center gap-10 text-center",
    ctaWrapper: "flex flex-row justify-center",
  },
});

const DeleteTeamMemberModal = ({
  isOpen,
  closeModal,
  email,
  publicKey,
}: IProps) => {
  const { t } = useTranslation();
  const { base, ctaWrapper } = styles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    mutate: deleteTeamMember,
    isPending: isDeleteTeamMemberPending,
    isSuccess: isDeleteTeamMemberSuccess,
  } = usePostDeleteTeamMember();

  useEffect(() => {
    if (isDeleteTeamMemberSuccess) {
      queryClient.removeQueries({ queryKey: ["settings", "team-members"] });
      toast.success(t("settings.userManagement.remove.success"));
      navigate("/settings/user-management");
    }
  }, [isDeleteTeamMemberSuccess]);

  const onClickDeleteTeamMember = () => {
    deleteTeamMember({ customerPublicKey: [publicKey], email });
  };

  const renderContent = () => (
    <div>{t("settings.userManagement.remove.content", { email })}</div>
  );

  const renderCta = () => (
    <div className={ctaWrapper()}>
      <Button
        as="button"
        variant="primary"
        onClick={onClickDeleteTeamMember}
        loading={isDeleteTeamMemberPending}>
        {t("settings.userManagement.remove.cta")}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggleIsOpen={closeModal} width="md">
      <div className={base()}>
        {renderContent()}
        {renderCta()}
      </div>
    </Modal>
  );
};

export default DeleteTeamMemberModal;
